import React from 'react'
import { UseFormRegister, UseFormHandleSubmit } from 'react-hook-form'
import { SlideThreeC, ThreeCFormData } from '../hooks/threec/types'
import { SlideContainer } from '../components/slide_container'
import { SlideHeader } from '../components/slide_header'
import {
  AiRegenerateSection,
  AiInstructionFormData,
} from '../components/ai_regenerate_section'
import { ListItem } from '../components/list/list_item'
import { ListSection } from '../components/list/list_section'
import logoGray from '../../../images/logo_gray.png'

interface ThreeCSlideProps {
  threeC: SlideThreeC
  isEditing: boolean
  isUpdating: boolean
  isDirty: boolean
  register: UseFormRegister<ThreeCFormData>
  handleSubmit: UseFormHandleSubmit<ThreeCFormData>
  registerAi: UseFormRegister<AiInstructionFormData>
  handleSubmitAi: UseFormHandleSubmit<AiInstructionFormData>
  aiInstruction: string
  onFormSubmit: (data: ThreeCFormData) => Promise<void>
  onAiRegenerate: (data: AiInstructionFormData) => Promise<void>
}

export const ThreeCSlide = React.forwardRef<HTMLDivElement, ThreeCSlideProps>(
  (
    {
      threeC,
      isEditing,
      isUpdating,
      isDirty,
      register,
      handleSubmit,
      registerAi,
      handleSubmitAi,
      aiInstruction,
      onFormSubmit,
      onAiRegenerate,
    },
    ref
  ) => {
    return (
      <div ref={ref}>
        <SlideContainer>
          <SlideHeader
            title="2. 3C分析"
            isEditing={isEditing}
            isUpdating={isUpdating}
            isDirty={isDirty}
            onUpdate={handleSubmit(onFormSubmit)}
          />
          <div className="slide-content px-8 py-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div>
                <p className="text-lg font-bold mb-4 underline">
                  ① Customer（市場・顧客）
                </p>
                <ListItem<ThreeCFormData>
                  label="ニーズ"
                  value={threeC.customer.needs}
                  isEditing={isEditing}
                  register={register}
                  fieldName="needs"
                />
                <ListSection<ThreeCFormData>
                  title="ターゲット層"
                  items={[
                    {
                      value: threeC.customer.targetFirst,
                      fieldName: 'targetFirst',
                    },
                    {
                      value: threeC.customer.targetSecond,
                      fieldName: 'targetSecond',
                    },
                  ]}
                  isEditing={isEditing}
                  register={register}
                />
                <ListSection<ThreeCFormData>
                  title="市場トレンド"
                  items={[
                    {
                      value: threeC.customer.marketTrendFirst,
                      fieldName: 'marketTrendFirst',
                    },
                    {
                      value: threeC.customer.marketTrendSecond,
                      fieldName: 'marketTrendSecond',
                    },
                  ]}
                  isEditing={isEditing}
                  register={register}
                />
              </div>
              <div>
                <p className="text-lg font-bold mb-4 underline">
                  ② Competitor（競合）
                </p>
                <ListSection<ThreeCFormData>
                  title="主要競合"
                  items={[
                    {
                      value: threeC.competitor.mainCompetitorFirst,
                      fieldName: 'mainCompetitorFirst',
                    },
                    {
                      value: threeC.competitor.mainCompetitorSecond,
                      fieldName: 'mainCompetitorSecond',
                    },
                  ]}
                  isEditing={isEditing}
                  register={register}
                />
                <ListSection<ThreeCFormData>
                  title="優位性と課題"
                  items={[
                    {
                      value: threeC.competitor.advantageFirst,
                      fieldName: 'advantageFirst',
                    },
                    {
                      value: threeC.competitor.advantageSecond,
                      fieldName: 'advantageSecond',
                    },
                  ]}
                  isEditing={isEditing}
                  register={register}
                />
                <ListSection<ThreeCFormData>
                  title="差別化のヒント"
                  items={[
                    {
                      value: threeC.competitor.differentiationFirst,
                      fieldName: 'differentiationFirst',
                    },
                    {
                      value: threeC.competitor.differentiationSecond,
                      fieldName: 'differentiationSecond',
                    },
                  ]}
                  isEditing={isEditing}
                  register={register}
                />
              </div>
              <div>
                <p className="text-lg font-bold mb-4 underline">
                  ③ Company（自社）
                </p>
                <ListSection<ThreeCFormData>
                  title="自社の強み"
                  items={[
                    {
                      value: threeC.company.strengthFirst,
                      fieldName: 'strengthFirst',
                    },
                    {
                      value: threeC.company.strengthSecond,
                      fieldName: 'strengthSecond',
                    },
                    {
                      value: threeC.company.strengthThird,
                      fieldName: 'strengthThird',
                    },
                  ]}
                  isEditing={isEditing}
                  register={register}
                />
                <ListSection<ThreeCFormData>
                  title="リソース"
                  items={[
                    {
                      value: threeC.company.resourceFirst,
                      fieldName: 'resourceFirst',
                    },
                    {
                      value: threeC.company.resourceSecond,
                      fieldName: 'resourceSecond',
                    },
                  ]}
                  isEditing={isEditing}
                  register={register}
                />
                <ListSection<ThreeCFormData>
                  title="今後の戦略"
                  items={[
                    {
                      value: threeC.company.strategyFirst,
                      fieldName: 'strategyFirst',
                    },
                    {
                      value: threeC.company.strategySecond,
                      fieldName: 'strategySecond',
                    },
                    {
                      value: threeC.company.strategyThird,
                      fieldName: 'strategyThird',
                    },
                  ]}
                  isEditing={isEditing}
                  register={register}
                />
              </div>
            </div>

            {isEditing && (
              <AiRegenerateSection
                register={registerAi}
                handleSubmit={handleSubmitAi}
                aiInstruction={aiInstruction}
                isUpdating={isUpdating}
                onSubmit={onAiRegenerate}
              />
            )}
            <div className="slide-footer mt-20">
              <p className="text-xs text-main-darkgray text-center mb-2">
                この企画書は「SDGs 共創
                Lodge」の共創アイディア生成AIで生成しました。
              </p>
              <img
                src={logoGray}
                alt="SDGs共創Lodge Logo"
                className="absolute bottom-2 right-4 w-20 h-auto"
              />
            </div>
          </div>
        </SlideContainer>
      </div>
    )
  }
)

ThreeCSlide.displayName = 'ThreeCSlide'
